import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api.service';
import { unwrapApiResponse } from '../../services/api/http-client.types';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';

export interface ShareLink {
  id: string;
  // Present only for callers with MANAGE on the artifact — the backend
  // strips the token-bearing URL for VIEW/EDIT grantees (they can see
  // that a link exists, but can't harvest a working public link).
  shareUrl?: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  viewCount: number;
}

const SHARE_LINKS_KEY = (artifactId: string) => ['shareLinks', artifactId] as const;

interface UseShareLinksOptions {
  // Override staleTime for callers that only need an indicator (e.g. "Shared" badge)
  // and don't need real-time view counts. Default 0 ensures the ShareModal always
  // fetches fresh data.
  staleTime?: number;
}

export function useShareLinks(artifactId: string, options?: UseShareLinksOptions) {
  const staleTime = options?.staleTime ?? 0;
  return useQuery({
    queryKey: SHARE_LINKS_KEY(artifactId),
    queryFn: async () => {
      const res = await apiService.get<ShareLink[]>(`/artifacts/${artifactId}/shares`);
      return res.data ?? [];
    },
    enabled: !!artifactId,
    staleTime,
    refetchOnMount: staleTime === 0 ? 'always' : true,
  });
}

export function useCreateShareLink() {
  const queryClient = useQueryClient();
  return useMutation({
    // Not the app's default single retry: a create is not idempotent, and a
    // refusal is an answer, not a blip. With the default, a free workspace's
    // 402 was sent twice a second apart, so the upgrade prompt and its
    // analytics fired twice.
    retry: false,
    // ApiResponseError keeps the HTTP status and code, so the caller can tell
    // a tier refusal (already answered by the upgrade prompt) from a failure.
    mutationFn: async (params: { artifactId: string; expiresAt?: string }) =>
      unwrapApiResponse(
        // retries: 0 — a non-idempotent POST (every call creates a new link);
        // the client's default retry replays it on 5xx/timeout, so a timeout
        // after the server committed would mint a second public link.
        await apiService.request<ShareLink>(
          {
            method: 'POST',
            url: `/artifacts/${params.artifactId}/share`,
            data: { expiresAt: params.expiresAt },
          },
          0,
        ),
        'Failed to create share link',
      ),
    onSuccess: (_, { artifactId }) => {
      safeAnalytics.setPersonProperty('last_share_link_created_at', new Date().toISOString());
      safeAnalytics.track(ANALYTICS_EVENTS.SHARE_LINK_CREATED, { artifactId });
    },
    // On ANY outcome, not just success: a create that times out after the
    // server committed still made a link. Refetching shows it, so the dialog
    // offers Copy and Disable for it. Without the refetch it keeps offering
    // "Get link", which the server refuses (SHARE_LINK_LIMIT_REACHED, one
    // active link per artifact) while the live link stays out of view.
    onSettled: (_data, _error, { artifactId }) => {
      void queryClient.invalidateQueries({ queryKey: SHARE_LINKS_KEY(artifactId) });
    },
  });
}

export function useRevokeShareLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { artifactId: string; shareLinkId: string }) => {
      const res = await apiService.delete<ShareLink>(
        `/artifacts/${params.artifactId}/shares/${params.shareLinkId}`,
      );
      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to revoke share link');
      }
      return res.data as ShareLink;
    },
    onSuccess: (_, { artifactId }) => {
      queryClient.invalidateQueries({ queryKey: SHARE_LINKS_KEY(artifactId) });
      safeAnalytics.track(ANALYTICS_EVENTS.SHARE_LINK_REVOKED, { artifactId });
    },
  });
}
